import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../shared/ui';
import './jobSeekerHelpModal.css';

export interface JobSeekerHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface StepItem {
  id: number;
  title: string;
  subtitle: string;
  badge: string;
  headline: string;
  description: string;
  bullets: string[];
  ctaLabel: string;
  ctaPath: string;
  graphicSvg: React.ReactNode;
}

export const JobSeekerHelpModal: React.FC<JobSeekerHelpModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);

  if (!isOpen) return null;

  const steps: StepItem[] = [
    {
      id: 1,
      title: 'AI Resume Audit',
      subtitle: 'ATS Keyword Check',
      badge: 'Step 1 • Diagnostic',
      headline: 'Pass the ATS Bot & Recruiter Bar',
      description:
        'Over 75% of job applications are silently rejected by automated Applicant Tracking Systems before a human ever reads them. The AI Resume Grader audits your resume against production hiring rubrics.',
      bullets: [
        'Identifies missing technical keywords for your target role',
        'Detects formatting issues that break parsing algorithms',
        'Scores recruiter impact metrics and gives actionable rewrite fixes',
      ],
      ctaLabel: 'Open AI Resume Grader',
      ctaPath: '/jobseeker-dashboard/resume-grader',
      graphicSvg: (
        <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="jsh-graphic-svg">
          <rect x="20" y="20" width="160" height="140" rx="10" fill="#141518" stroke="#26272e" strokeWidth="2" />
          <line x1="40" y1="40" x2="100" y2="40" stroke="#dcb974" strokeWidth="4" strokeLinecap="round" />
          <line x1="40" y1="60" x2="150" y2="60" stroke="#3b3d47" strokeWidth="3" strokeLinecap="round" />
          <line x1="40" y1="75" x2="130" y2="75" stroke="#3b3d47" strokeWidth="3" strokeLinecap="round" />
          <line x1="40" y1="90" x2="145" y2="90" stroke="#3b3d47" strokeWidth="3" strokeLinecap="round" />
          <line x1="40" y1="115" x2="110" y2="115" stroke="#3b3d47" strokeWidth="3" strokeLinecap="round" />
          <line x1="40" y1="130" x2="140" y2="130" stroke="#3b3d47" strokeWidth="3" strokeLinecap="round" />

          {/* Scanner beam */}
          <line x1="15" y1="85" x2="185" y2="85" stroke="#22c55e" strokeWidth="2" strokeDasharray="4 2" />
          <rect x="18" y="70" width="164" height="30" fill="url(#scannerGlow)" opacity="0.25" />

          {/* Score Badge */}
          <circle cx="240" cy="90" r="45" fill="#141518" stroke="#dcb974" strokeWidth="3" />
          <circle cx="240" cy="90" r="38" stroke="#22c55e" strokeWidth="3" strokeDasharray="180 50" />
          <text x="240" y="85" textAnchor="middle" fill="#fafafa" fontSize="22" fontWeight="bold" fontFamily="system-ui">88</text>
          <text x="240" y="105" textAnchor="middle" fill="#9ca3af" fontSize="10" fontFamily="system-ui">ATS SCORE</text>

          <defs>
            <linearGradient id="scannerGlow" x1="100" y1="70" x2="100" y2="100" gradientUnits="userSpaceOnUse">
              <stop stopColor="#22c55e" />
              <stop offset="1" stopColor="#22c55e" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      id: 2,
      title: 'Verified Referral Card',
      subtitle: 'Proof of Legitimacy',
      badge: 'Step 2 • Trust Layer',
      headline: 'Prove What You Actually Built',
      description:
        'Referrers ignore strangers on LinkedIn because resumes are full of exaggerated buzzwords. Your Verified Referral Card stores concrete, checkable technical achievements backed by working professionals.',
      bullets: [
        'Stated as Action / System / Result so skills stay checkable',
        'Attach GitHub repo links, live production demos, or benchmarks',
        'Unlocks the 🛡️ Vouched Candidate Trust Seal on your profile',
      ],
      ctaLabel: 'Build Your Verified Card',
      ctaPath: '/jobseeker-dashboard/career-proof',
      graphicSvg: (
        <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="jsh-graphic-svg">
          {/* Card Shield Graphic */}
          <rect x="50" y="25" width="220" height="130" rx="14" fill="#141518" stroke="#dcb974" strokeWidth="2" />
          <circle cx="90" cy="65" r="20" fill="#20180a" stroke="#dcb974" strokeWidth="2" />
          <path d="M90 55 L98 60 L98 70 Q90 77 90 77 Q82 70 82 60 Z" fill="#dcb974" />
          <text x="122" y="60" fill="#fafafa" fontSize="13" fontWeight="bold" fontFamily="system-ui">Peer Vouched</text>
          <text x="122" y="75" fill="#4ade80" fontSize="10" fontFamily="system-ui">✓ Verified by SDE @ Swiggy</text>

          <rect x="70" y="100" width="180" height="20" rx="6" fill="#1e1f25" />
          <circle cx="82" cy="110" r="4" fill="#22c55e" />
          <text x="94" y="114" fill="#d1d5db" fontSize="9" fontFamily="system-ui">Redis Caching (45% p99 latency cut)</text>

          <rect x="70" y="125" width="180" height="20" rx="6" fill="#1e1f25" />
          <circle cx="82" cy="135" r="4" fill="#22c55e" />
          <text x="94" y="139" fill="#d1d5db" fontSize="9" fontFamily="system-ui">React Virtualized Dashboard (98+ Perf)</text>
        </svg>
      ),
    },
    {
      id: 3,
      title: '1:1 Live Calibration',
      subtitle: 'Mock Screen & Vouch',
      badge: 'Step 3 • Mentorship',
      headline: 'Practice Live with Target Insiders',
      description:
        'Connect with verified Senior Engineers and Tech Leads from Google, Swiggy, Uber, and Razorpay. Review your resume, practice live system design, and earn verified attestations.',
      bullets: [
        '15-Minute Culture & Fit Calls or 45-Minute Deep Mock Interviews',
        'Working professionals coach you on exact company interview loops',
        'Mentors submit a 60-second attestation that vouches for your skills',
      ],
      ctaLabel: 'Browse Verified Mentors',
      ctaPath: '/jobseeker-dashboard/resume-review',
      graphicSvg: (
        <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="jsh-graphic-svg">
          {/* Candidate Node */}
          <circle cx="80" cy="90" r="32" fill="#141518" stroke="#3b3d47" strokeWidth="2" />
          <text x="80" y="95" textAnchor="middle" fill="#fafafa" fontSize="12" fontWeight="bold" fontFamily="system-ui">YOU</text>

          {/* Connection Arc */}
          <path d="M120 90 L200 90" stroke="#dcb974" strokeWidth="2" strokeDasharray="4 4" />
          <circle cx="160" cy="90" r="14" fill="#20180a" stroke="#dcb974" strokeWidth="1.5" />
          <text x="160" y="94" textAnchor="middle" fill="#dcb974" fontSize="10" fontFamily="system-ui">1:1</text>

          {/* Mentor Node */}
          <circle cx="240" cy="90" r="32" fill="#141518" stroke="#22c55e" strokeWidth="2" />
          <text x="240" y="86" textAnchor="middle" fill="#fafafa" fontSize="11" fontWeight="bold" fontFamily="system-ui">SDE-3</text>
          <text x="240" y="100" textAnchor="middle" fill="#4ade80" fontSize="9" fontFamily="system-ui">@ Google</text>
        </svg>
      ),
    },
    {
      id: 4,
      title: 'Weekly Referral Drops',
      subtitle: 'Direct ATS Submission',
      badge: 'Step 4 • The Outcome',
      headline: 'Get Referred Directly by Insiders',
      description:
        'Skip the cold-messaging grind. Every week, batch drops open for top product companies. Internal employees review your vouched card, 30s voice pitch, and submit your referral to their internal ATS.',
      bullets: [
        'Batch drops sent directly to active employee referrers',
        'Includes your Job Req ID, 30s Voice Pitch, and Verified Proof',
        'Guaranteed review with 72-hour turnaround and zero ghosting',
      ],
      ctaLabel: 'View Active Drops',
      ctaPath: '/jobseeker-dashboard/referral-drops',
      graphicSvg: (
        <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="jsh-graphic-svg">
          {/* Drop Rocket Illustration */}
          <rect x="30" y="40" width="100" height="100" rx="12" fill="#141518" stroke="#26272e" strokeWidth="2" />
          <text x="80" y="70" textAnchor="middle" fill="#fafafa" fontSize="11" fontWeight="bold" fontFamily="system-ui">YOUR CARD</text>
          <text x="80" y="88" textAnchor="middle" fill="#dcb974" fontSize="9" fontFamily="system-ui">🛡️ Vouched</text>
          <text x="80" y="104" textAnchor="middle" fill="#4ade80" fontSize="9" fontFamily="system-ui">🎙️ 30s Voice Pitch</text>

          {/* Fast Track Arrow */}
          <path d="M140 90 L180 90" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrow)" />

          {/* Target Company Portal */}
          <rect x="190" y="30" width="105" height="120" rx="12" fill="#141518" stroke="#22c55e" strokeWidth="2" />
          <rect x="202" y="45" width="80" height="20" rx="4" fill="#20180a" stroke="#dcb974" />
          <text x="242" y="58" textAnchor="middle" fill="#e7c684" fontSize="9" fontWeight="bold" fontFamily="system-ui">SWIGGY ATS</text>
          <text x="242" y="85" textAnchor="middle" fill="#fafafa" fontSize="10" fontWeight="bold" fontFamily="system-ui">FAST-TRACK</text>
          <text x="242" y="100" textAnchor="middle" fill="#22c55e" fontSize="9" fontFamily="system-ui">✓ Referral Submitted</text>
          <text x="242" y="125" textAnchor="middle" fill="#9ca3af" fontSize="8" fontFamily="system-ui">Interview Invite</text>
        </svg>
      ),
    },
  ];

  const current = steps.find((s) => s.id === activeStep) || steps[0];

  const handleCta = (path: string) => {
    onClose();
    navigate(path);
  };

  return createPortal(
    <div className="jsh-modal-overlay" onClick={onClose}>
      <div className="jsh-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="jsh-header">
          <div className="jsh-header-title-box">
            <div className="jsh-header-icon-glow">
              <span style={{ fontSize: '1.4rem' }}>🚀</span>
            </div>
            <div>
              <h2 className="jsh-header-title">How to Get Hired on ConnectX</h2>
              <p className="jsh-header-sub">The 4-step fast-track blueprint to land interviews without ATS ghosting</p>
            </div>
          </div>

          <button type="button" className="jsh-close-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* 4-Step Interactive Flow Steps Bar */}
        <div className="jsh-flow-banner">
          <div className="jsh-flow-steps-bar">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`jsh-step-tab ${activeStep === step.id ? 'is-active' : ''}`}
                onClick={() => setActiveStep(step.id)}
              >
                <div className="jsh-step-tab-num">{step.id}</div>
                <div className="jsh-step-tab-text">
                  <span className="jsh-step-tab-title">{step.title}</span>
                  <span className="jsh-step-tab-subtitle">{step.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable Content Body */}
        <div className="jsh-body">
          {/* Active Step Showcase */}
          <div className="jsh-showcase-card">
            <div className="jsh-showcase-left">
              <span className="jsh-step-badge-pill">
                <span>✦</span> {current.badge}
              </span>
              <h3 className="jsh-showcase-title">{current.headline}</h3>
              <p className="jsh-showcase-desc">{current.description}</p>

              <div className="jsh-showcase-bullets">
                {current.bullets.map((b, idx) => (
                  <div key={idx} className="jsh-bullet-row">
                    <span className="jsh-bullet-icon">✓</span>
                    <span>{b}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '12px' }}>
                <Button variant="primary" onClick={() => handleCta(current.ctaPath)}>
                  {current.ctaLabel} →
                </Button>
              </div>
            </div>

            <div className="jsh-graphic-frame">
              {current.graphicSvg}
            </div>
          </div>

          {/* 3 Golden Rules for Job Seekers */}
          <div className="jsh-pro-tips-section">
            <h4 className="jsh-pro-tips-heading">
              <span>💡</span> 3 Secrets to 100% Referral Acceptance
            </h4>

            <div className="jsh-tips-grid">
              <div className="jsh-tip-card">
                <h5 className="jsh-tip-title">
                  <span>🎙️</span> Always Add a 30s Voice Pitch
                </h5>
                <p className="jsh-tip-desc">
                  Referrers review cards with recorded voice pitches first. A confident 30-second summary doubles your pickup rate.
                </p>
              </div>

              <div className="jsh-tip-card">
                <h5 className="jsh-tip-title">
                  <span>🛡️</span> Get 1 Vouched Claim
                </h5>
                <p className="jsh-tip-desc">
                  Having even 1 verified working professional vouch for your code separates you from 2,000 cold applicants.
                </p>
              </div>

              <div className="jsh-tip-card">
                <h5 className="jsh-tip-title">
                  <span>⚡</span> Notice Period Calibration
                </h5>
                <p className="jsh-tip-desc">
                  Immediate joiners (≤ 30 days) get priority review from referrers at high-growth tech companies.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="jsh-footer">
          <span className="jsh-footer-left">
            Need personalized help? Book a quick 1:1 prep call with any verified insider.
          </span>

          <div className="jsh-footer-actions">
            <Button variant="ghost" onClick={onClose}>
              Close Guide
            </Button>
            <Button
              variant="secondary"
              onClick={() => setActiveStep((prev) => (prev < 4 ? prev + 1 : 1))}
            >
              {activeStep < 4 ? `Next: Step ${activeStep + 1} →` : 'Back to Step 1 ↺'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default JobSeekerHelpModal;
